/**
 * ProfileTimeline — Sprint 17g-4 Task 5.13
 *
 * Vertical timeline of profile evolution milestones:
 * accuracy thresholds, entity discoveries, trends, pipeline runs, summary updates.
 */

import { cn } from '@ownyou/ui-design-system';
import type { TimelineEvent, TimelineIcon } from '@/hooks/useProfileTimeline';

export interface ProfileTimelineProps {
  events: TimelineEvent[];
}

const ICON_MAP: Record<TimelineIcon, { symbol: string; color: string }> = {
  milestone: { symbol: '\u2605', color: 'text-amber-500' },    // ★
  discovery: { symbol: '\u2726', color: 'text-indigo-400' },   // ✦
  trend:     { symbol: '\u2197', color: 'text-emerald-500' },  // ↗
  pipeline:  { symbol: '\u27F3', color: 'text-sky-400' },      // ⟳
  profile:   { symbol: '\u25C9', color: 'text-violet-400' },   // ◉
};

function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function ProfileTimeline({ events }: ProfileTimelineProps) {
  if (events.length === 0) return null;

  return (
    <div data-testid="profile-timeline" className="space-y-0">
      {events.map((event, idx) => {
        const iconConfig = ICON_MAP[event.icon];
        const isLast = idx === events.length - 1;

        return (
          <div key={event.id} className="flex gap-3" data-testid={`timeline-event-${event.type}`}>
            {/* Vertical line + icon */}
            <div className="flex flex-col items-center w-6 shrink-0">
              <span className={cn('text-sm leading-6', iconConfig.color)}>
                {iconConfig.symbol}
              </span>
              {!isLast && <div className="w-px flex-1 bg-neutral-100" />}
            </div>

            {/* Content */}
            <div className={cn('pb-4 flex-1 min-w-0', isLast && 'pb-0')}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm text-neutral-700 font-medium truncate">
                  {event.headline}
                </p>
                <span className="text-[10px] text-neutral-300 shrink-0">
                  {timeAgo(event.timestamp)}
                </span>
              </div>
              {event.detail && (
                <p className="text-xs text-neutral-400 mt-0.5">{event.detail}</p>
              )}
              {event.tokens && event.tokens > 0 && (
                <span className="inline-block text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded mt-1">
                  +{event.tokens} tokens
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
